import React from "react";
import { cn } from "../utils/cn";

interface TrialBannerProps {
  /** "compact" for sign-in callout, "banner" for dashboard/onboarding */
  variant: "compact" | "banner";
  /** Current trial day (1-7+) — 0 means pre-trial */
  trialDay: number;
  /** Total trial days */
  totalDays: number;
  /** Handler for "Compare plans" CTA */
  onUpgrade: () => void;
  /** Handler for "Dismiss" (Days 1-5 only) */
  onDismiss?: () => void;
  /** Whether banner can be dismissed */
  dismissible?: boolean;
}

/**
 * Determine the visual state of the banner based on trial day.
 */
function getBannerState(
  trialDay: number,
  totalDays: number
): "active" | "urgency" | "expired" {
  if (trialDay > totalDays) return "expired";
  if (trialDay >= 6) return "urgency";
  return "active";
}

/**
 * Build the display text based on trial day and state.
 */
function getBannerText(
  trialDay: number,
  totalDays: number,
  daysRemaining: number,
  state: "active" | "urgency" | "expired"
): React.ReactNode {
  if (state === "expired") {
    return "Your free trial has ended. Upgrade to keep all features.";
  }

  const isLastDay = trialDay === totalDays;

  // Days 1–3: friendly
  if (trialDay <= 3) {
    return (
      <>
        🎁 Free trial: Day {trialDay} of {totalDays} — {daysRemaining}{" "}
        {daysRemaining === 1 ? "day" : "days"} left.
      </>
    );
  }

  // Days 4–5: encouraging
  if (trialDay <= 5) {
    return (
      <>
        ⏰ Trial ending soon: {daysRemaining}{" "}
        {daysRemaining === 1 ? "day" : "days"} left to decide.
      </>
    );
  }

  // Day 6 (last day): urgent
  if (isLastDay) {
    return (
      <>
        ⚠️ <strong>Last day!</strong> Your trial ends tomorrow.
      </>
    );
  }

  // Day 7 (just before expiry)
  return (
    <>
      ⚠️ <strong>Last day!</strong> Day {trialDay} of {totalDays} —{" "}
      {daysRemaining} day remaining
    </>
  );
}

/**
 * TrialBanner — Reusable trial countdown banner with progress bar.
 *
 * Renders in three states with escalating visual urgency:
 *   - Active (Days 1-5): Friendly/encouraging, dismissible
 *   - Urgency (Days 6-7): Prominent, non-dismissible
 *   - Expired (Day 8+): Alert, non-dismissible
 */
export const TrialBanner: React.FC<TrialBannerProps> = ({
  variant,
  trialDay,
  totalDays,
  onUpgrade,
  onDismiss,
  dismissible = false,
}) => {
  const state = getBannerState(trialDay, totalDays);
  const daysRemaining = Math.max(0, totalDays - trialDay + 1);
  const progressPercent = Math.min((trialDay / totalDays) * 100, 100);

  return (
    <div
      className={cn(
        "trial-banner",
        `trial-banner-${state}`,
        variant === "compact" && "trial-banner-compact"
      )}
      id="trial-banner"
      role="status"
    >
      <div className="trial-banner-content">
        <p className="trial-banner-text">
          {getBannerText(trialDay, totalDays, daysRemaining, state)}
        </p>

        <div className="trial-banner-actions">
          <button
            className="trial-banner-upgrade"
            onClick={onUpgrade}
            aria-label="Compare plans"
          >
            Compare plans →
          </button>

          {dismissible && (
            <button
              className="trial-banner-dismiss"
              onClick={onDismiss}
              aria-label="Dismiss trial banner"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Visual progress bar (not shown when expired) */}
      {state !== "expired" && (
        <div className="trial-banner-progress">
          <div
            className="trial-banner-progress-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}
    </div>
  );
};
